import { useMemo } from "react";

import type { GenericGraphProps } from "@similarweb/pro-logic-components";
import { LineGraph, ColumnChartGraph, CombinedTypeGraph } from "@similarweb/pro-logic-components";
import { LegendBullet } from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import { abbrFloatingNumberFilter, percentageSignOnlyFilter } from "app/filters/ngFilters";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import ChartStreamService from "app/pages/sales-intelligence/modules/ai-agent/services/chart/chartService";
import type {
    ChartDataFormat,
    ChartObject,
    ChartSeries,
    ChartTrendSeries,
} from "app/pages/sales-intelligence/modules/ai-agent/services/chart/types";
import type { AiAgentEmailAutocompleteCompany } from "app/pages/sales-intelligence/modules/ai-agent/types/email";

const BulletLegends = ({ data }: { data: ChartTrendSeries[] }) => (
    <Stack>
        {data.map(({ color, name }) => (
            <LegendBullet
                text={name}
                labelColor={color}
                size={0}
            />
        ))}
    </Stack>
);

const SimplifiedBulletLegends = ({ data }: { data: ChartTrendSeries[] }) => (
    <LegendBullet
        text={data[0].name}
        labelColor={data[0].color}
        size={0}
    />
);

const getLegendProps = (isSimplifiedView: boolean) => ({
    withMarkers: false,
    isSelectableCheckboxes: false,
    LegendComponent: isSimplifiedView ? SimplifiedBulletLegends : BulletLegends,
    legendPosition: isSimplifiedView ? ("top" as const) : ("right" as const),
    legendsMaxWidth: 300,
    legendsMinWidth: 110,
    legendsWithValueMaxWidth: 110,
    shouldPresentValueAtLegend: true,
    graphLegendPaddingLeft: 0,
});

const formatValue = (val: number, format?: ChartDataFormat) => {
    if (val === 0) {
        return "0";
    }
    if (format === "percentage") {
        return percentageSignOnlyFilter()(val, 0);
    }
    if (format === "number" || format === "raw" || format === "currency") {
        return String(abbrFloatingNumberFilter()(val));
    }
    return format ? `$${val} ${format}` : String(val);
};

const makeYAxis = ({
    format,
    isSecondary,
    isSimplifiedView,
}: {
    format?: ChartDataFormat;
    isSecondary: boolean;
    isSimplifiedView: boolean;
}) => ({
    title: { text: undefined },
    labels: {
        enabled: !isSimplifiedView,
        formatter: ({ value }: { value: number | string }) => {
            return typeof value === "number" ? formatValue(value, format) : value;
        },
        useHTML: true,
    },
    opposite: isSecondary,
    visible: true,
});

export const GraphChart = ({
    chartObject,
    chartData,
    isSimplifiedView = false,
    selectedCompany,
}: {
    chartObject: ChartObject;
    chartData: ChartTrendSeries[] | GenericGraphProps;
    isSimplifiedView?: boolean;
    selectedCompany?: AiAgentEmailAutocompleteCompany | null;
}) => {
    const { series, chart, xAxis, xargs } = chartObject;
    const isBarLike = chart.type === "bar" || chart.type === "column";

    const formats = useMemo(
        () => Array.from(new Set((series ?? []).map((s: ChartSeries) => s?.dataFormat ?? "number"))),
        [series],
    );
    const isMixed = useMemo(() => ChartStreamService.hasMixedFormat(chartObject), [chartObject]);

    const config = useMemo(() => {
        const primaryFormat = formats[0] ?? series?.[0]?.dataFormat;
        const secondaryFormat = formats[1] ?? series?.[1]?.dataFormat;

        const yAxis =
            isMixed && isBarLike
                ? [
                      makeYAxis({ format: primaryFormat, isSecondary: false, isSimplifiedView }),
                      makeYAxis({ format: secondaryFormat, isSecondary: true, isSimplifiedView }),
                  ]
                : makeYAxis({ format: primaryFormat, isSecondary: false, isSimplifiedView });

        return {
            yAxis,
            xAxis: {
                tickInterval: isBarLike ? 1 : undefined,
                labels: {
                    enabled: !isSimplifiedView,
                    formatter({ value }: { value: number | string }): string {
                        const category = xAxis?.categories?.[Number(value)];
                        const meta = xargs?.domains?.find(({ domain }) => domain === category);
                        if (selectedCompany?.domain === category) {
                            return selectedCompany?.companyName || category || (value as string);
                        }
                        if (meta?.companyName) {
                            return meta.companyName;
                        }
                        if (typeof value !== "number") {
                            return value;
                        }
                        return xAxis?.categories?.[value] || value.toString();
                    },
                    useHTML: true,
                    step: isBarLike ? 1 : undefined,
                    rotation: isBarLike ? -45 : undefined,
                },
            },
            chart: {
                height: isSimplifiedView ? 88 : 168,
                width: 235,
                animation: false,
                plotBackgroundColor: "transparent",
                alignTicks: isMixed ? false : undefined,
            },
            tooltip: { enabled: false },
            plotOptions: {
                series: {
                    enableMouseTracking: false,
                    states: { hover: { enabled: false } },
                    animation: false,
                    connectNulls: false,
                },
            },
        };
    }, [isSimplifiedView, selectedCompany, chartObject]);

    const sharedLegendProps = useMemo(() => getLegendProps(isSimplifiedView), [isSimplifiedView]);

    if (isMixed && isBarLike) {
        return (
            <CombinedTypeGraph
                graphConfig={config}
                data={chartData as GenericGraphProps}
                {...sharedLegendProps}
                isSingleSelectionMode={false}
                plotOptionsKeys={[chartTypes.COLUMN, chartTypes.COLUMN]}
            />
        );
    }

    if (isBarLike) {
        return (
            <ColumnChartGraph
                data={chartData as ChartTrendSeries[]}
                graphConfig={config}
                {...sharedLegendProps}
            />
        );
    }

    if (chart.type === "line") {
        return (
            <LineGraph
                data={chartData as ChartTrendSeries[]}
                graphConfig={config}
                {...sharedLegendProps}
            />
        );
    }
};
